import React from 'react';
import { Divider, Icon } from 'semantic-ui-react';
import styled from 'styled-components';

interface PageHeaderProps {
  title: React.ReactNode;
  titleIcon?: React.ReactNode;
  titleIconBg?: string;
  caption?: string;
  anchor?: React.ReactNode;
  controls?: React.ReactNode;
  divider?: boolean;
  backText?: string;
  onBack?: () => void;
}

interface PageSectionProps {
  padleft?: boolean | string;
  padright?: boolean | string;
  padtop?: boolean | string;
  padbottom?: boolean | string;
  shadow?: boolean;
  width?: string;
  display?: string;
  gap?: string;
  flex?: string;
  flexDirection?: string;
  justifyContent?: string;
  alignItems?: string;
}

export const PageSection = styled.div<PageSectionProps>`
  ${props => props.width && `width: ${props.width};`} ;
  ${props => props.gap && `gap: ${props.gap};`} ;

  ${props => props.display && `display: ${props.display};`} ;
  ${props => props.flex && `flex: ${props.flex};`} ;
  ${props => props.flexDirection && `flex-direction: ${props.flexDirection};`} ;
  ${props => props.justifyContent && `justify-content: ${props.justifyContent};`} ;
  ${props => props.alignItems && `align-items: ${props.alignItems};`} ;

  ${props =>
    props.padtop && `padding-top: ${typeof props.padtop === 'string' ? props.padtop : '20px'};`} ;
  ${props =>
    props.padright &&
    `padding-right: ${typeof props.padright === 'string' ? props.padright : '25px'};`} ;
  ${props =>
    props.padbottom &&
    `padding-bottom: ${typeof props.padbottom === 'string' ? props.padbottom : '20px'};`} ;
  ${props =>
    props.padleft &&
    `padding-left: ${typeof props.padleft === 'string' ? props.padleft : '25px'};`} ;

  ${props => props.shadow && `box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;`} ;
`;

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  titleIcon,
  titleIconBg,
  caption,
  anchor,
  controls,
  divider,
  backText,
  onBack,
}) => (
  <>
    <HeaderSection
      gap="15px"
      padbottom={divider ? '0' : '15px'}
      display="flex"
      justifyContent="flex-start"
      alignItems="center"
      width="100%"
    >
      {anchor}
      <PageSection flex="12" justifyContent="flex-start" alignItems="center">
        {backText && onBack && (
          <BackRow>
            <BackButton onClick={onBack} type="button">
              <Icon name="chevron left" />
              {backText}
            </BackButton>
          </BackRow>
        )}
        <TitleAndControlsRow>
          <TitleRow>
            {titleIcon && <TitleIcon bg={titleIconBg}>{titleIcon}</TitleIcon>}
            <PageSection>
              <Title>{title}</Title>
              {caption && <Caption>{caption}</Caption>}
            </PageSection>
          </TitleRow>
          {controls && <HeaderControlsContainer>{controls}</HeaderControlsContainer>}
        </TitleAndControlsRow>
      </PageSection>
    </HeaderSection>
    {divider && <Divider />}
  </>
);

const TitleAndControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const BackRow = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 16px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  color: #757f8e;
  font-family: 'Poppins';
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  line-height: 1.2;

  &:hover {
    color: #5f70ff;
  }

  svg,
  i {
    margin: 0;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

interface ControlContainerProps {
  gap?: boolean;
  justify?: 'left' | 'right' | 'center';
  flex?: string;
}

export const ControlContainer = styled.div<ControlContainerProps>`
  display: flex;
  flex: ${({ flex }) => flex || '1'};
  width: 50%;
  flex-direction: row;
  align-items: center;
  gap: ${({ gap = true }) => (gap ? '8px' : '0')};
  justify-content: ${({ justify }) => {
    switch (justify) {
      case 'left':
        return 'flex-start';
      case 'right':
        return 'flex-end';
      case 'center':
        return 'center';
      default:
        return 'flex-end';
    }
  }};
`;

const HeaderControlsContainer = styled(ControlContainer)`
  width: auto;
  flex: 0 0 auto;

  @media (max-width: 520px) {
    width: 100%;
    justify-content: flex-start;
    margin-top: 0;
  }
`;

const HeaderSection = styled(PageSection)`
  align-items: flex-start;

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: stretch;

    ${ControlContainer} {
      width: 100%;
      justify-content: flex-start;
      margin-top: 12px;
    }
  }
`;

const TitleIcon = styled.div<{ bg?: string }>`
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${({ bg }) => bg || '#5f70ff'};
  color: #ffffff;
  font-size: 1.1rem;
  line-height: 0;

  svg,
  i {
    position: relative;
    top: 8px;
    left: 2px;
  }
`;

export const PageSubheader: React.FC<PageHeaderProps> = ({ title, controls }) => (
  <HeaderContainer>
    <Subtitle>{title}</Subtitle>
    {controls && <ControlContainer>{controls}</ControlContainer>}
  </HeaderContainer>
);

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
  padding-bottom: 15px;
`;

interface ControlContainerRowProps {
  padleft?: boolean | string;
  padright?: boolean | string;
  padtop?: boolean | string;
  padbottom?: boolean | string;
}

export const ControlContainerRow = styled.div<ControlContainerRowProps>`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  ${props =>
    props.padtop && `padding-top: ${typeof props.padtop === 'string' ? props.padtop : '8px'};`};
  ${props =>
    props.padright &&
    `padding-right: ${typeof props.padright === 'string' ? props.padright : '10px'};`};
  ${props =>
    props.padbottom &&
    `padding-bottom: ${typeof props.padbottom === 'string' ? props.padbottom : '8px'};`};
  ${props =>
    props.padleft &&
    `padding-left: ${typeof props.padleft === 'string' ? props.padleft : '10px'};`};
`;

export const PageContent = styled.div.withConfig({
  shouldForwardProp: (prop: any) => prop === 'data-testid' || !prop.startsWith('data-'),
})`
  font-family: 'Poppins';
  font-style: normal;
  padding: 16px 20px 20px;
  width: 100%;
  height: 100%;
  overflow: auto;
  box-sizing: border-box;
  background-color: transparent;
`;

interface TitleProps {
  color?: string;
  padleft?: boolean | string;
  padright?: boolean | string;
  padtop?: boolean | string;
  padbottom?: boolean | string;
}

export const Title = styled.div<TitleProps>`
  position: relative;
  z-index: 2;
  font-family: 'Poppins';
  font-size: 22px;
  font-weight: 500;
  font-style: normal;
  color: ${props => props.color || '#414141'};
  ${props =>
    props.padtop && `padding-top: ${typeof props.padtop === 'string' ? props.padtop : '20px'};`};
  ${props =>
    props.padright &&
    `padding-right: ${typeof props.padright === 'string' ? props.padright : '25px'};`};
  ${props =>
    props.padbottom &&
    `padding-bottom: ${typeof props.padbottom === 'string' ? props.padbottom : '20px'};`};
  ${props =>
    props.padleft &&
    `padding-left: ${typeof props.padleft === 'string' ? props.padleft : '25px'};`};
`;

interface SubtitleProps {
  color?: string;
  padbottom?: string;
}

export const Subtitle = styled.div<SubtitleProps>`
  font-family: 'Poppins';
  font-size: 18px;
  font-weight: 500;
  font-style: normal;
  color: ${props => props.color || '#414141'};
  padding-bottom: ${props => props.padbottom || '10px'};
`;


interface H2Props {
  color?: string;
  padleft?: boolean | string;
  padright?: boolean | string;
  padtop?: boolean | string;
  padbottom?: boolean | string;
}

export const H2 = styled.div<H2Props>`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 1px;
  color: ${props => props.color || '#414141'};
  ${props =>
    props.padtop && `padding-top: ${typeof props.padtop === 'string' ? props.padtop : '20px'};`};
  ${props =>
    props.padright &&
    `padding-right: ${typeof props.padright === 'string' ? props.padright : '25px'};`};
  ${props =>
    props.padbottom &&
    `padding-bottom: ${typeof props.padbottom === 'string' ? props.padbottom : '10px'};`};
  ${props =>
    props.padleft &&
    `padding-left: ${typeof props.padleft === 'string' ? props.padleft : '25px'};`};
`;


export const H3 = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 1px;
`;

export const H4 = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #333;
  margin-bottom: 1px;
`;

export const H5 = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #777;
  margin-bottom: 1px;
`;


export const DialogPrompt = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #333;
  margin-bottom: 4px;
`;

export const BodyText = styled.div<{
  color?: string;
  padtop?: boolean | string;
  padbottom?: boolean | string;
}>`
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  color: ${props => props.color || '#777'}; // Default to #777 if no color is provided
  ${props =>
    props.padtop && `padding-top: ${typeof props.padtop === 'string' ? props.padtop : '10px'};`};
  ${props =>
    props.padbottom &&
    `padding-bottom: ${typeof props.padbottom === 'string' ? props.padbottom : '10px'};`};
`;

export const FieldLabel = styled.div`
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 400;
  width: 250px;
  align-self: center;
  text-align: left;
  font-size: 14px;
  line-height: 18px;
  color: #333;
  margin-bottom: 5px;
`;

export const FieldLabelTop = styled.div`
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 550;
  width: 250px;
  align-self: center;
  text-align: left;
  font-size: 13px;
  line-height: 18px;
  color: #333;
  margin-bottom: 5px;
`;


export const FormRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  margin-bottom: 10px;
`;

export const Caption = styled.div`
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  max-width: 1200px;
  color: #777;
`;

export const StyledCard = styled.div`
  flex-grow: 1;
  border-radius: 6px;
  padding: 5px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;

  overflow: hidden;
  text-overflow: ellipsis;
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    cursor: pointer;
  }
`;


export const ModalFieldLabel = styled.div`
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 550;
  width: 250px;
  align-self: center;
  text-align: left;
  font-size: 12px;
  line-height: 18px;
  color: #333;
  margin-bottom: 3px;
`;

